import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircleOutlined, 
  MessageOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CarOutlined,
  RadarChartOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  RocketOutlined,
  SearchOutlined,
  TagsOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import axios from 'axios';
import { useAuth } from '../../../../../contexts/AuthContext';

const ReviewAndLaunch = ({ campaignData }) => {
  const navigate = useNavigate();
  const { token, currentUser } = useAuth();
  const [launching, setLaunching] = useState(false);
  
  // Check if this is a Meta Ads campaign (stop or route type with adPlatform)
  const isMetaAdsCampaign = (campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
                            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'google' || campaignData.adPlatform === 'both');
  
  const isEmail = campaignData.campaignChannel === 'email';
  const isBoth = campaignData.campaignChannel === 'both';

  // Get selected segments info
  const getAudienceInfo = () => {
    if (campaignData.audienceMethod === 'quick' && campaignData.selectedSegments?.length > 0) {
      return {
        type: 'Quick Select Segments',
        segments: campaignData.selectedSegments,
        reach: 2757, // Mock calculated reach
      };
    }
    if (campaignData.audienceMethod === 'custom') {
      const hasFilters = campaignData.customFilters?.service?.length > 0 || 
                         campaignData.customFilters?.geo?.length > 0;
      return {
        type: 'Dynamic Builder Filters',
        configured: hasFilters,
        reach: hasFilters ? 5420 : 0,
      };
    }
    return { type: 'Not configured', reach: 0 };
  };

  const audienceInfo = getAudienceInfo();

  // Get delivery settings
  const getCooldownLabel = (value) => {
    const labels = {
      '7days': '7 days', '14days': '14 days', '30days': '30 days',
      '45days': '45 days', '60days': '60 days', '90days': '90 days',
      '120days': '120 days', '150days': '150 days', '180days': '180 days',
      '1year': '1 year', '18months': '18 months', '24months': '24 months',
      '36months': '36 months', '48months': '48 months',
    };
    return labels[value] || '18 months';
  };

  const getRangeLabel = (value) => {
    const labels = {
      500: '500ft', 1000: '1000ft', 2500: '2500ft', 5000: '5000ft',
      5280: '1 mi', 10560: '2 mi', 26400: '5 mi',
    };
    return labels[value] || `${value}ft`;
  };

  const handleLaunchCampaign = async () => {
    if (launching) return;

    // For Meta Ads or Google Ads campaigns, use different validation and API
    if (isMetaAdsCampaign) {
      const metaAds = campaignData.metaAds || {};
      const googleAds = campaignData.googleAds || {};
      
      // Validate Google Ads campaign
      if (campaignData.adPlatform === 'google') {
        if (!googleAds.campaignName) {
          message.error('Google Ads campaign name is required');
          return;
        }

        if (!googleAds.schedule || googleAds.schedule.length !== 2) {
          message.error('Campaign schedule is required');
          return;
        }

        setLaunching(true);

        try {
          const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

          // Validate that a Google Ads account was selected
          if (!googleAds.selectedAccountId) {
            message.error('Please select a Google Ads account in the campaign configuration');
            setLaunching(false);
            return;
          }

          // Prepare Google Ads campaign payload
          const payload = {
            name: googleAds.campaignName,
            description: `${campaignData.campaignType.toUpperCase()} GOOGLE Campaign`,
            status: 'active',
            campaign_type: campaignData.campaignType,
            ad_platform: 'google',
            route_data: {
              locations: campaignData.selectedLocations || [],
              vehicles: campaignData.selectedVehicles || [],
              stops: campaignData.selectedStops || [],
              routes: campaignData.selectedRoutes || [],
            },
            google_ads_config: {
              selected_account_id: googleAds.selectedAccountId,
              budget_name: googleAds.budgetName,
              daily_budget: googleAds.dailyBudget,
              delivery_method: googleAds.deliveryMethod,
              campaign_name: googleAds.campaignName,
              channel_type: googleAds.channelType,
              bidding_strategy: googleAds.biddingStrategy,
              target_cpa: googleAds.targetCpa,
              target_roas: googleAds.targetRoas,
              start_date: googleAds.schedule[0]?.toISOString(),
              end_date: googleAds.schedule[1]?.toISOString(),
              target_google_search: googleAds.targetGoogleSearch,
              target_search_network: googleAds.targetSearchNetwork,
              target_content_network: googleAds.targetContentNetwork,
              ad_group_name: googleAds.adGroupName,
              cpc_bid: googleAds.cpcBid,
              keywords: googleAds.keywords,
              final_url: googleAds.finalUrl,
              path1: googleAds.path1,
              path2: googleAds.path2,
              headlines: googleAds.headlines,
              descriptions: googleAds.descriptions,
              location: googleAds.location,
              language: googleAds.language,
            },
          };

          // Save campaign to database
          const response = await axios.post('/api/vehicle-campaigns', payload, {
            headers: authHeaders,
          });

          if (response.data) {
            const campaignId = response.data.id;
            message.success('Campaign saved! Creating in Google Ads...');
            
            try {
              // Create campaign in Google Ads via backend API
              const googleAdsResponse = await axios.post(
                '/api/google-ads/create-campaign',
                {
                  campaign_id: campaignId,
                  account_id: googleAds.selectedAccountId,
                  config: payload.google_ads_config
                },
                { headers: authHeaders }
              );

              const customerId = googleAdsResponse.data?.customer_id;
              const googleCampaignId = googleAdsResponse.data?.google_campaign_id;

              // Build redirect URL with fallbacks when fields are missing
              let googleAdsUrl;
              if (googleCampaignId && customerId) {
                googleAdsUrl = `https://ads.google.com/aw/campaigns?campaignId=${googleCampaignId}&customerId=${customerId}`;
              } else if (customerId) {
                googleAdsUrl = `https://ads.google.com/aw/campaigns?__c=${customerId}`;
              } else {
                googleAdsUrl = 'https://ads.google.com/aw/campaigns';
              }

              if (googleCampaignId) {
                message.success('Campaign created in Google Ads successfully!');
              } else {
                message.warning('Campaign saved locally. Please create it manually in Google Ads.');
              }

              // Open Google Ads BEFORE setTimeout — popup blockers reject window.open
              // called from a delayed callback (loses the user-gesture context)
              window.open(googleAdsUrl, '_blank');
              setTimeout(() => navigate('/client/campaigns/new-camp-demo'), 1500);
            } catch (googleAdsError) {
              console.error('Error creating campaign in Google Ads:', googleAdsError);
              const gErr = googleAdsError?.response?.data;
              const detail = gErr?.error_details || gErr?.details || gErr?.error || googleAdsError?.message;
              message.error(detail
                ? `Google Ads error: ${detail}`
                : 'Campaign saved locally. Please create it manually in Google Ads.');
              window.open('https://ads.google.com/aw/campaigns', '_blank');
              setTimeout(() => navigate('/client/campaigns/new-camp-demo'), 1500);
            }
          }
        } catch (error) {
          console.error('Error launching Google Ads campaign:', error);
          if (error.response?.status === 404) {
            message.error('Please connect your Google Ads account first in Integrations');
          } else {
            const errorMsg = error?.response?.data?.error || error.message || 'Failed to launch campaign';
            message.error(errorMsg);
          }
        } finally {
          setLaunching(false);
        }
        return;
      }
      
      // Validate Meta Ads campaign
      if (!metaAds.name) {
        message.error('Meta Ads campaign name is required');
        return;
      }

      if (!metaAds.schedule || metaAds.schedule.length !== 2) {
        message.error('Campaign schedule is required');
        return;
      }

      setLaunching(true);

      try {
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        // Check if Facebook is connected
        const fbConnectionResponse = await axios.get('/api/integrations/facebook', {
          headers: authHeaders,
        });

        if (!fbConnectionResponse.data || !fbConnectionResponse.data.access_token) {
          message.error('Please connect your Facebook account first in Integrations');
          setLaunching(false);
          return;
        }

        // Prepare Meta Ads campaign payload
        const payload = {
          name: metaAds.name,
          description: `${campaignData.campaignType.toUpperCase()} ${campaignData.adPlatform.toUpperCase()} Campaign`,
          status: 'active',
          campaign_type: campaignData.campaignType, // 'stop' or 'route'
          ad_platform: campaignData.adPlatform, // 'meta', 'google', or 'both'
          route_data: {
            locations: campaignData.selectedLocations || [],
            vehicles: campaignData.selectedVehicles || [],
            stops: campaignData.selectedStops || [],
            routes: campaignData.selectedRoutes || [],
            selectedStopsData: campaignData.selectedStopsData || [],
            selectedRoutesData: campaignData.selectedRoutesData || [],
          },
          meta_ads_config: {
            name: metaAds.name,
            objective: metaAds.objective,
            budget_type: metaAds.budgetType,
            daily_budget: metaAds.dailyBudget,
            total_budget: metaAds.totalBudget,
            start_date: metaAds.schedule[0]?.toISOString(),
            end_date: metaAds.schedule[1]?.toISOString(),
            headline: metaAds.headline,
            description: metaAds.description,
            image_url: metaAds.imageUrl,
            cta: metaAds.cta,
            radius_km: metaAds.radiusKm,
            locations: metaAds.locations || [],
            location_addresses: metaAds.locationAddresses || [],
          },
        };

        // Create Meta Ads campaign via API
        const response = await axios.post('/api/vehicle-campaigns', payload, {
          headers: authHeaders,
        });

        if (response.data) {
          message.success('Meta Ads Campaign saved successfully! Redirecting to Facebook Ads Manager...');
          
          // Build Facebook Ads Manager URL with campaign parameters
          const fbAdsUrl = new URL('https://business.facebook.com/adsmanager/manage/campaigns');
          
          // Add campaign parameters
          fbAdsUrl.searchParams.append('campaign_name', metaAds.name);
          fbAdsUrl.searchParams.append('objective', metaAds.objective);
          fbAdsUrl.searchParams.append('budget', metaAds.budgetType === 'daily' ? metaAds.dailyBudget : metaAds.totalBudget);
          fbAdsUrl.searchParams.append('budget_type', metaAds.budgetType);
          
          // Add location data if available
          if (metaAds.locations && metaAds.locations.length > 0) {
            const locationData = metaAds.locations.map(loc => ({
              latitude: loc.latitude,
              longitude: loc.longitude,
              radius: metaAds.radiusKm,
              distance_unit: 'kilometer'
            }));
            fbAdsUrl.searchParams.append('geo_locations', JSON.stringify(locationData));
          }
          
          // Redirect to Facebook Ads Manager after short delay
          setTimeout(() => {
            window.open(fbAdsUrl.toString(), '_blank');
            navigate('/client/campaigns');
          }, 1500);
        }
      } catch (error) {
        console.error('Error launching Meta Ads campaign:', error);
        const errorMsg = error?.response?.data?.error || error.message || 'Failed to launch campaign';
        message.error(errorMsg);
      } finally {
        setLaunching(false);
      }
      return;
    }

    // Original SMS/Email campaign validation
    if (!campaignData.campaignName) {
      message.error('Campaign name is required');
      return;
    }

    if (!campaignData.campaignChannel) {
      message.error('Please select a campaign channel');
      return;
    }

    const messageContent = isEmail ? campaignData.emailBody : campaignData.smsMessage;
    if (!messageContent) {
      message.error(`Please provide ${isEmail ? 'email' : 'SMS'} message content`);
      return;
    }

    // For email campaigns, check we have recipients
    if (isEmail || isBoth) {
      const hasRecipients = (campaignData.uploadedCustomers?.length > 0) ||
                            (campaignData.selectedSegments?.length > 0);
      if (!hasRecipients) {
        message.error('Please upload a CSV or select at least one audience segment');
        return;
      }
    }

    setLaunching(true);

    try {
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Save campaign to database first
      const payload = {
        name: campaignData.campaignName,
        description: `${campaignData.campaignChannel.toUpperCase()} ProximityIQ Campaign`,
        status: 'active',
        route_data: {
          campaign_type: 'proximity',
          channel: campaignData.campaignChannel,
          locations: campaignData.selectedLocations || [],
          vehicles: campaignData.selectedVehicles || [],
          notification_range: campaignData.notificationRange || 1000,
          audience_method: campaignData.audienceMethod || 'quick',
          selected_segments: campaignData.selectedSegments || [],
          custom_filters: campaignData.customFilters || {},
          sms_message: isBoth || !isEmail ? campaignData.smsMessage : null,
          email_subject: isBoth || isEmail ? campaignData.subjectLine || campaignData.emailSubject : null,
          email_body: isBoth || isEmail ? campaignData.emailBody : null,
          cooldown_period: campaignData.cooldownPeriod || null,
          quiet_hours_enabled: campaignData.quietHoursEnabled !== false,
          quiet_hours_from: campaignData.quietHoursFrom || '9:00 PM',
          quiet_hours_to: campaignData.quietHoursTo || '8:00 AM',
        },
      };

      const saveResponse = await axios.post('/api/vehicle-campaigns', payload, { headers: authHeaders });
      const campaignId = saveResponse.data?.id;

      // Send email if email or both channel
      if (isEmail || isBoth) {
        // Build recipients list: CSV upload takes priority, then segment contacts
        let recipients = [];
        if (campaignData.uploadedCustomers?.length > 0) {
          // Use CSV-uploaded contacts that have email addresses
          recipients = campaignData.uploadedCustomers
            .filter(c => c.email && c.email.includes('@'))
            .map(c => ({ name: c.name || '', email: c.email }));
        } else {
          // Segment-based: we don't have actual contact emails yet,
          // so send to the reply-to/sender email as a test
          const testEmail = campaignData.replyToEmail || null;
          if (testEmail) {
            recipients = [{ name: campaignData.senderName || 'Test', email: testEmail }];
          }
        }

        if (recipients.length > 0) {
          message.loading({ content: `Sending emails to ${recipients.length} contacts...`, key: 'email-send', duration: 0 });
          try {
            const emailResponse = await axios.post(
              '/api/email-campaigns/send',
              {
                campaign_id: campaignId,
                recipients,
                subject: campaignData.subjectLine || campaignData.emailSubject || campaignData.campaignName,
                body: campaignData.emailBody,
                sender_name: campaignData.senderName,
                sender_email: campaignData.senderEmail || currentUser?.email,
                reply_to: campaignData.replyToEmail || campaignData.senderEmail || currentUser?.email,
                logo_url: campaignData.logoUrl || null,
                cooldown_period: campaignData.cooldownPeriod || null,
                quiet_hours_enabled: campaignData.quietHoursEnabled !== false,
                quiet_hours_from: campaignData.quietHoursFrom || '9:00 PM',
                quiet_hours_to: campaignData.quietHoursTo || '8:00 AM',
              },
              { headers: authHeaders }
            );

            const { sent, failed, skipped, total, skipped_quiet_hours, message: apiMsg } = emailResponse.data;
            if (skipped_quiet_hours) {
              message.warning({ content: `⏰ ${apiMsg}`, key: 'email-send', duration: 8 });
            } else {
              const skippedNote = skipped > 0 ? `, ${skipped} skipped (cooldown)` : '';
              message.success({ content: `✅ Emails sent: ${sent}/${total} delivered${failed > 0 ? `, ${failed} failed` : ''}${skippedNote}`, key: 'email-send', duration: 5 });
            }
          } catch (emailErr) {
            const emailErrMsg = emailErr?.response?.data?.error || emailErr.message;
            message.warning({ content: `Campaign saved, but email sending failed: ${emailErrMsg}`, key: 'email-send', duration: 6 });
          }
        } else {
          message.warning('Campaign saved. No valid email addresses found to send to.');
        }
      } else {
        message.success('SMS Campaign launched successfully!');
      }

      setTimeout(() => {
        navigate('/client/campaigns/new-camp-demo');
      }, 2000);

    } catch (error) {
      console.error('Error launching campaign:', error);
      const errorMsg = error?.response?.data?.error || error.message || 'Failed to launch campaign';
      message.error(errorMsg);
    } finally {
      setLaunching(false);
    }
  };

  // Get message preview
  const getMessagePreview = () => {
    const content = isEmail ? campaignData.emailBody : campaignData.smsMessage;
    let preview = content || '{{service_date}}';
    preview = preview.replace(/\{\{first_name\}\}/g, 'John');
    preview = preview.replace(/\{\{last_name\}\}/g, 'Smith');
    preview = preview.replace(/\{\{service_date\}\}/g, 'Dec 15');
    preview = preview.replace(/\{\{service_type\}\}/g, 'HVAC Maintenance');
    return preview;
  };

  const charCount = (campaignData.smsMessage || '{{service_date}}').length;
  const smsSegments = Math.ceil(charCount / 160) || 1;

  // If this is a Meta Ads or Google Ads campaign, show appropriate review
  if (isMetaAdsCampaign) {
    const metaAds = campaignData.metaAds || {};
    const googleAds = campaignData.googleAds || {};
    const platformLabel = campaignData.adPlatform === 'meta' ? 'Meta Ads' : 
                         campaignData.adPlatform === 'google' ? 'Google Ads' : 'Meta & Google Ads';
    
    // Show Google Ads review if it's a Google-only campaign
    if (campaignData.adPlatform === 'google') {
      return (
        <div className="step-container review-step">
          <div className="review-header-icon">
            <CheckCircleOutlined />
          </div>
          <h1 className="step-title">Review & Launch</h1>
          <p className="step-subtitle">Review your {platformLabel} campaign settings before launching</p>

          <div className="review-cards">
            {/* Campaign Info Card */}
            <div className="review-card">
              <div className="review-card-header">
                <SearchOutlined />
                <span className="review-card-title">{googleAds.campaignName || 'Google Ads Campaign'}</span>
              </div>
              <div className="review-card-stats">
                <span><strong>Channel:</strong> {googleAds.channelType || 'SEARCH'}</span>
                <span><strong>Budget:</strong> ${googleAds.dailyBudget || 50}/day</span>
                <span><strong>Bidding:</strong> {googleAds.biddingStrategy || 'MAXIMIZE_CONVERSIONS'}</span>
              </div>
            </div>

            {/* Ad Creative Card */}
            <div className="review-card">
              <div className="review-card-header">
                <MessageOutlined style={{ color: '#22c55e' }} />
                <div>
                  <span className="review-card-title">Ad Creative</span>
                  <span className="review-card-subtitle">Headlines & Descriptions</span>
                </div>
              </div>
              <div className="message-preview-box">
                <p><strong>Headlines:</strong></p>
                {googleAds.headlines?.filter(h => h).map((h, i) => (
                  <p key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>• {h}</p>
                ))}
                <p style={{ marginTop: '12px' }}><strong>Descriptions:</strong></p>
                {googleAds.descriptions?.filter(d => d).map((d, i) => (
                  <p key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>• {d}</p>
                ))}
              </div>
              <div className="message-stats">
                Final URL: {googleAds.finalUrl || 'Not set'}
              </div>
            </div>

            {/* Keywords Card (if Search campaign) */}
            {googleAds.channelType === 'SEARCH' && googleAds.keywords?.length > 0 && (
              <div className="review-card">
                <div className="review-card-header">
                  <TagsOutlined style={{ color: '#f59e0b' }} />
                  <div>
                    <span className="review-card-title">Keywords</span>
                    <span className="review-card-subtitle">{googleAds.keywords.length} keywords</span>
                  </div>
                </div>
                <div className="segment-tags">
                  {googleAds.keywords.slice(0, 10).map((kw, i) => (
                    <span key={i} className="segment-tag">{kw.text} ({kw.matchType})</span>
                  ))}
                  {googleAds.keywords.length > 10 && (
                    <span className="segment-tag">+{googleAds.keywords.length - 10} more</span>
                  )}
                </div>
              </div>
            )}

            {/* Schedule & Targeting Card */}
            <div className="review-card">
              <div className="review-card-header">
                <ClockCircleOutlined style={{ color: '#f59e0b' }} />
                <div>
                  <span className="review-card-title">Schedule & Targeting</span>
                </div>
              </div>
              <div className="delivery-info">
                <div className="delivery-item">
                  <ClockCircleOutlined />
                  <span>Start Date</span>
                  <strong>{googleAds.schedule?.[0]?.format('MMM D, YYYY') || 'Not set'}</strong>
                </div>
                <div className="delivery-item">
                  <ClockCircleOutlined />
                  <span>End Date</span>
                  <strong>{googleAds.schedule?.[1]?.format('MMM D, YYYY') || 'Not set'}</strong>
                </div>
                <div className="delivery-item">
                  <GlobalOutlined />
                  <span>Location</span>
                  <strong>{googleAds.location || 'Not set'}</strong>
                </div>
                <div className="delivery-item">
                  <GlobalOutlined />
                  <span>Language</span>
                  <strong>{googleAds.language || 'en'}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Launch Button */}
          <div className="campaign-footer">
            <div className="footer-info"></div>
            <button 
              className="launch-btn" 
              onClick={handleLaunchCampaign}
              disabled={launching}
            >
              <RocketOutlined />
              {launching ? 'Launching...' : `Launch ${platformLabel} Campaign`}
            </button>
          </div>
        </div>
      );
    }
    
    // Show Meta Ads review
    return (
      <div className="step-container review-step">
        <div className="review-header-icon">
          <CheckCircleOutlined />
        </div>
        <h1 className="step-title">Review & Launch</h1>
        <p className="step-subtitle">Review your {platformLabel} campaign settings before launching</p>

        <div className="review-cards">
          {/* Campaign Info Card */}
          <div className="review-card">
            <div className="review-card-header">
              <TeamOutlined />
              <span className="review-card-title">{metaAds.name || 'Meta Ads Campaign'}</span>
            </div>
            <div className="review-card-stats">
              <span><strong>Objective:</strong> {metaAds.objective || 'REACH'}</span>
              <span><strong>Budget:</strong> ${metaAds.budgetType === 'daily' ? metaAds.dailyBudget : metaAds.totalBudget} ({metaAds.budgetType || 'daily'})</span>
              <span><strong>Radius:</strong> {metaAds.radiusKm || 5} km</span>
            </div>
          </div>

          {/* Creative Card */}
          <div className="review-card">
            <div className="review-card-header">
              <MessageOutlined style={{ color: '#22c55e' }} />
              <div>
                <span className="review-card-title">Ad Creative</span>
                <span className="review-card-subtitle">Headline & Description</span>
              </div>
            </div>
            <div className="message-preview-box">
              <p><strong>{metaAds.headline || 'No headline set'}</strong></p>
              <p>{metaAds.description || 'No description set'}</p>
              {metaAds.imageUrl && <p style={{ fontSize: '12px', color: '#666' }}>Image: {metaAds.imageUrl}</p>}
            </div>
            <div className="message-stats">
              CTA: {metaAds.cta || 'LEARN_MORE'}
            </div>
          </div>

          {/* Schedule Card */}
          <div className="review-card">
            <div className="review-card-header">
              <ClockCircleOutlined style={{ color: '#f59e0b' }} />
              <div>
                <span className="review-card-title">Campaign Schedule</span>
                <span className="review-card-subtitle">Start and end dates</span>
              </div>
            </div>
            <div className="delivery-info">
              <div className="delivery-item">
                <ClockCircleOutlined />
                <span>Start Date</span>
                <strong>{metaAds.schedule?.[0]?.format('MMM D, YYYY') || 'Not set'}</strong>
              </div>
              <div className="delivery-item">
                <ClockCircleOutlined />
                <span>End Date</span>
                <strong>{metaAds.schedule?.[1]?.format('MMM D, YYYY') || 'Not set'}</strong>
              </div>
            </div>
          </div>

          {/* Location Targeting Card */}
          {metaAds.locationAddresses && metaAds.locationAddresses.length > 0 && (
            <div className="review-card">
              <div className="review-card-header">
                <GlobalOutlined style={{ color: 'var(--ink)' }} />
                <div>
                  <span className="review-card-title">Location Targeting</span>
                  <span className="review-card-subtitle">{metaAds.locationAddresses.length} location{metaAds.locationAddresses.length > 1 ? 's' : ''} • {metaAds.radiusKm}km radius</span>
                </div>
              </div>
              <div style={{ 
                maxHeight: 150, 
                overflowY: 'auto',
                padding: '12px 0'
              }}>
                {metaAds.locationAddresses.map((addr, idx) => (
                  <div key={idx} style={{
                    padding: '6px 0',
                    fontSize: 14,
                    color: '#475569',
                    borderBottom: idx < metaAds.locationAddresses.length - 1 ? '1px solid #e2e8f0' : 'none'
                  }}>
                    📍 {addr}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Targeting Card */}
          <div className="review-card">
            <div className="review-card-header">
              <EnvironmentOutlined />
              <strong>Targeting</strong>
            </div>
            <p>
              Ads will target people within {metaAds.radiusKm || 5} km of your {campaignData.campaignType === 'stop' ? 'stop' : 'route'} locations.
            </p>
            <div className="review-card-stats">
              <span><CarOutlined /> {campaignData.selectedVehicles?.length || 0} vehicles</span>
              <span><EnvironmentOutlined /> {campaignData.selectedLocations?.length || 0} locations</span>
            </div>
          </div>
        </div>

        {/* Launch Button */}
        <div className="campaign-footer">
          <div className="footer-info"></div>
          <button 
            className="launch-btn" 
            onClick={handleLaunchCampaign}
            disabled={launching}
          >
            <RocketOutlined />
            {launching ? 'Launching...' : `Launch ${platformLabel} Campaign`}
          </button>
        </div>
      </div>
    );
  }

  // Original SMS/Email campaign review
  return (
    <div className="step-container review-step">
      <div className="review-header-icon">
        <CheckCircleOutlined />
      </div>
      <h1 className="step-title">Review & Launch</h1>
      <p className="step-subtitle">Review your {isBoth ? 'SMS & Email' : isEmail ? 'email' : 'SMS'} campaign settings before launching</p>

      <div className="review-cards">
        {/* Campaign Info Card */}
        <div className="review-card">
          <div className="review-card-header">
            <MessageOutlined />
            <span className="review-card-title">{campaignData.campaignName || 'www'}</span>
          </div>
          <div className="review-card-stats">
            <span><EnvironmentOutlined /> {campaignData.selectedLocations?.length || 1} location</span>
            <span><CarOutlined /> {campaignData.selectedVehicles?.length || 1} vehicle</span>
            <span><RadarChartOutlined /> {getRangeLabel(campaignData.notificationRange || 1000)} trigger range</span>
          </div>
        </div>

        {/* Target Audience Card */}
        <div className="review-card">
          <div className="review-card-header">
            <TeamOutlined />
            <div>
              <span className="review-card-title">Target Audience</span>
              <span className="review-card-subtitle">{audienceInfo.type}</span>
            </div>
          </div>
          
          {campaignData.audienceMethod === 'quick' && campaignData.selectedSegments?.length > 0 && (
            <div className="segment-tags">
              {campaignData.selectedSegments.includes('recent-service') && (
                <span className="segment-tag">Recent Service (90 days)</span>
              )}
              {campaignData.selectedSegments.includes('active-members') && (
                <span className="segment-tag">Active Members</span>
              )}
              {campaignData.selectedSegments.includes('opted-in') && (
                <span className="segment-tag">All Opted-In Contacts</span>
              )}
              {campaignData.selectedSegments.includes('new-subscribers') && (
                <span className="segment-tag">New Subscribers</span>
              )}
            </div>
          )}

          {campaignData.audienceMethod === 'custom' && !audienceInfo.configured && (
            <div className="no-audience-warning">No audience configured</div>
          )}

          <div className="estimated-reach-row">
            <span>Estimated Reach</span>
            <strong>{audienceInfo.reach.toLocaleString()} contacts</strong>
          </div>
        </div>

        {/* Message Content Card */}
        <div className="review-card">
          <div className="review-card-header">
            {isEmail ? <MailOutlined style={{ color: '#22c55e' }} /> : <MessageOutlined style={{ color: '#22c55e' }} />}
            <div>
              <span className="review-card-title">Message Content</span>
              <span className="review-card-subtitle">{isEmail ? 'Email Message' : 'SMS Text Message'}</span>
            </div>
          </div>
          <div className="message-preview-box">
            <p>{getMessagePreview()}</p>
            <span className="opt-out-text">{isEmail ? 'Unsubscribe link included' : 'Reply STOP to unsubscribe'}</span>
          </div>
          {!isEmail && (
            <div className="message-stats">
              {charCount} characters · {smsSegments} SMS segment
            </div>
          )}
        </div>

        {/* Delivery Settings Card */}
        <div className="review-card">
          <div className="review-card-header">
            <ClockCircleOutlined style={{ color: '#f59e0b' }} />
            <div>
              <span className="review-card-title">Delivery Settings</span>
              <span className="review-card-subtitle">Frequency and timing controls</span>
            </div>
          </div>
          <div className="delivery-info">
            <div className="delivery-item">
              <ClockCircleOutlined />
              <span>Cooldown Period</span>
              <strong>{getCooldownLabel(campaignData.cooldownPeriod)}</strong>
            </div>
            <div className="delivery-item">
              <ClockCircleOutlined />
              <span>Quiet Hours</span>
              <strong>{campaignData.quietHoursFrom || '9:00 PM'} - {campaignData.quietHoursTo || '8:00 AM'}</strong>
            </div>
          </div>
        </div>

        {/* Compliance Card */}
        <div className="review-card tcpa-card">
          <div className="review-card-header">
            <SafetyOutlined />
            <strong>{isEmail ? 'CAN-SPAM Compliant' : 'TCPA Compliant'}</strong>
          </div>
          <p>
            {isEmail 
              ? 'All messages will include opt-out instructions. Contacts who reply STOP will be automatically removed from future messaging.'
              : 'All messages will include opt-out instructions. Contacts who reply STOP will be automatically removed from future messaging.'
            }
          </p>
        </div>
      </div>

      {/* Launch Button */}
      <button 
        className="launch-sms-btn" 
        onClick={handleLaunchCampaign}
        disabled={launching}
      >
        <RocketOutlined />
        {launching ? 'Launching...' : `Launch ${isBoth ? 'SMS & Email' : isEmail ? 'Email' : 'SMS'} Campaign`}
      </button>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info"></div>
        <button 
          className="launch-btn" 
          onClick={handleLaunchCampaign}
          disabled={launching}
        >
          <RocketOutlined />
          {launching ? 'Launching...' : 'Launch Campaign'}
        </button>
      </div>
    </div>
  );
};

export default ReviewAndLaunch;
